/**
 * Refresh Stage 8: Section Drafting
 *
 * Fan-out stage: drafts each section from the brief individually.
 * Skips "keep" sections. Shows action tags (update/rewrite/new) per card.
 * Same pattern as creation Stage4bSectionDraftingV2 but reads from
 * refresh.brief.sections and uses extractionId for per-section artifacts.
 */

import React, { useCallback, useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { blogRunsService } from '../../../services/blogRunsService';
import { useBatchExecution } from '../shared/useBatchExecution';
import type { ArticleDocument } from '../types';

const ACTION_COLORS: Record<string, string> = {
  keep: 'bg-gray-100 text-gray-500',
  update: 'bg-blue-100 text-blue-700',
  rewrite: 'bg-amber-100 text-amber-700',
  new: 'bg-green-100 text-green-700',
};

function SectionCards({ onSelectSection }: { onSelectSection: (id: string) => void }) {
  const { document, run, refreshRun } = useArticleContext();
  const brief = (document as any)?.refresh?.brief;
  const sections = brief?.sections || [];

  const handleBeforeRun = useCallback(async () => {
    if (!run?.run_id) return;
    await blogRunsService.resetFromStage(run.run_id, 'refresh_8_drafting');
    await refreshRun();
  }, [run?.run_id, refreshRun]);

  const {
    selectedId, setSelectedId,
    runningAll, batchStatus, runningSingle,
    runSingle, runAll, cancelBatch,
  } = useBatchExecution({
    stageId: 'refresh_8_drafting',
    onBeforeBatch: handleBeforeRun,
    onBeforeSingle: handleBeforeRun,
  });

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    onSelectSection(id);
  }, [setSelectedId, onSelectSection]);

  const draftableSections = sections.filter((s: any) => s.action !== 'keep');

  const selectedSection = sections.find((s: any) => s.id === selectedId);
  const draftedCount = sections.filter((s: any) => s.drafted_body || s.action === 'keep').length;

  return (
    <div className="space-y-3">
      {/* Batch controls */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
          disabled={runningAll || draftableSections.length === 0}
          onClick={() => runAll(draftableSections.map((s: any) => s.id))}
        >
          {runningAll ? 'Drafting All...' : `Draft All (${draftableSections.length})`}
        </button>
        {runningAll && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={cancelBatch}
          >
            Cancel
          </button>
        )}
        <span className="text-xs text-gray-400">
          {draftedCount}/{sections.length} ready
        </span>
      </div>

      {/* Section cards */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {sections.map((section: any) => {
          const hasDraft = Boolean(section.drafted_body) || section.action === 'keep';
          const batch = batchStatus[section.id];
          return (
            <button
              key={section.id}
              type="button"
              className={`flex-shrink-0 border rounded px-3 py-2 text-left text-xs w-44 ${
                selectedId === section.id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              } ${hasDraft ? 'bg-green-50' : 'bg-white'}`}
              onClick={() => handleSelect(section.id)}
            >
              <div className="font-medium text-gray-800 truncate">
                {section.heading || '(Intro)'}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${ACTION_COLORS[section.action] || ''}`}>
                  {section.action}
                </span>
                {section.target_word_count > 0 && (
                  <span className="text-gray-400">{section.target_word_count}w</span>
                )}
              </div>
              {batch && (
                <div className={`text-[10px] mt-1 ${
                  batch.status === 'succeeded' ? 'text-green-600' :
                  batch.status === 'failed' ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {batch.status}
                </div>
              )}
              {!batch && hasDraft && (
                <div className="text-[10px] mt-1 text-green-600">
                  {section.action === 'keep' ? 'kept' : 'drafted'}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected section detail */}
      {selectedSection && (
        <div className="border rounded p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-gray-800">
              {selectedSection.heading || '(Intro)'} — {selectedSection.heading_level}
              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${ACTION_COLORS[selectedSection.action] || ''}`}>
                {selectedSection.action}
              </span>
            </div>
            {selectedSection.action !== 'keep' && (
              <button
                type="button"
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                disabled={runningSingle || runningAll}
                onClick={() => runSingle(selectedSection.id)}
              >
                {runningSingle ? 'Drafting...' : 'Draft Section'}
              </button>
            )}
          </div>
          {selectedSection.instructions && (
            <div className="text-xs text-gray-500 mb-1">{selectedSection.instructions}</div>
          )}
          {selectedSection.drafted_body ? (
            <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-2 max-h-60 overflow-y-auto">
              {selectedSection.drafted_body}
            </pre>
          ) : selectedSection.action === 'keep' ? (
            <div className="text-xs text-gray-400">Kept unchanged — existing content passes through to assembly.</div>
          ) : (
            <div className="text-xs text-gray-400">Not yet drafted.</div>
          )}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const brief = (doc as any)?.refresh?.brief;
  const sections = brief?.sections || [];
  if (sections.length === 0) {
    return <div className="text-sm text-gray-500">No sections to draft.</div>;
  }
  const kept = sections.filter((s: any) => s.action === 'keep').length;
  const drafted = sections.filter((s: any) => s.drafted_body).length;
  const totalWords = sections.reduce((sum: number, s: any) => {
    const text = s.drafted_body || (s.action === 'keep' ? s.existing_content : '') || '';
    return sum + text.split(/\s+/).filter(Boolean).length;
  }, 0);
  return (
    <div className="text-xs text-gray-600">
      {kept} kept, {drafted} drafted of {sections.length} sections — ~{totalWords} words total
    </div>
  );
}

export function RefreshDraftingV2() {
  const { document } = useArticleContext();
  const brief = (document as any)?.refresh?.brief;
  const hasBrief = Boolean(brief?.sections?.length);
  const [selectedExtractionId, setSelectedExtractionId] = useState<string | undefined>(undefined);

  return (
    <StagePanel
      stageId="refresh_8_drafting"
      isLlmStage
      extractionId={selectedExtractionId}
      title="Refresh: Section Drafting"
      description="Drafts each section from the brief. Keeps unchanged sections, updates or rewrites others."
      hideRunButton
      renderOutput={renderOutput}
    >
      {hasBrief ? <SectionCards onSelectSection={setSelectedExtractionId} /> : (
        <div className="text-xs text-gray-500">No brief yet. Run the Brief stage first.</div>
      )}
    </StagePanel>
  );
}
